import React, { useState, useEffect } from 'react';
import { Container, Nav, Navbar } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FaHome, FaBuilding, FaUsers, FaTools, FaChartBar, FaArrowLeft, FaPhone, FaInfoCircle, FaTachometerAlt, FaMoneyBillWave, FaDollarSign, FaTag, FaListAlt, FaHandshake, FaBars, FaWhatsapp, FaSignOutAlt } from 'react-icons/fa';
import { useLanguage } from '../../context/LanguageContext';
import Translation from '../../components/common/Translation';
import './AdminLayout.scss';

const AdminLayout = ({ children }) => {
  const [expanded, setExpanded] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setExpanded(!expanded);
    }
  };

  const closeMobileSidebar = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };
  
  const handleLogout = async () => {
    try {
      const adminToken = localStorage.getItem('adminToken');
      
      if (adminToken) {
        // Call the logout API
        await fetch('/api/admin/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${adminToken}`
          }
        });
      }
      
      // Clear admin authentication from localStorage
      localStorage.removeItem('adminToken');
      localStorage.removeItem('isAdmin');
      
      // Redirect to admin login page
      navigate('/admin/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Still clear local storage and redirect even if API call fails
      localStorage.removeItem('adminToken');
      localStorage.removeItem('isAdmin');
      navigate('/admin/login');
    }
  };

  const menuItems = [
    { path: '/admin', icon: <FaTachometerAlt />, labelKey: 'dashboard' },
    { path: '/admin/home', icon: <FaHome />, labelKey: 'home' },
    { path: '/admin/about', icon: <FaInfoCircle />, labelKey: 'about' },
    { path: '/admin/contact', icon: <FaPhone />, labelKey: 'contact' },
    { path: '/admin/properties', icon: <FaBuilding />, labelKey: 'properties' },
    { path: '/admin/users', icon: <FaUsers />, labelKey: 'users' },
    { path: '/admin/maintenance', icon: <FaTools />, labelKey: 'maintenance' },
    { path: '/admin/whatsapp', icon: <FaWhatsapp />, labelKey: 'whatsapp' },
    { path: '/admin/income', icon: <FaDollarSign />, labelKey: 'income' },
    { path: '/admin/analytics', icon: <FaChartBar />, labelKey: 'analytics' },
  ];

  return (
    <div className="admin-layout">
      {/* Mobile Menu Toggle */}
      {isMobile && (
        <button
          className="mobile-menu-toggle btn btn-dark d-md-none"
          onClick={toggleSidebar}
          style={{
            position: 'fixed',
            top: '1rem',
            left: '1rem',
            zIndex: 1001,
            padding: '0.5rem',
            borderRadius: '4px',
          }}
        >
          <FaBars />
        </button>
      )}

      {/* Mobile Overlay */}
      <div
        className={`mobile-sidebar-overlay ${mobileOpen ? 'active' : ''}`}
        onClick={closeMobileSidebar}
      />

      <Navbar 
        className={`admin-sidebar ${expanded ? '' : 'collapsed'} ${mobileOpen ? 'mobile-open' : ''}`}
        bg="dark" 
        variant="dark" 
        expand="lg"
      >
        <Container fluid className="d-flex flex-column align-items-start p-0 h-100">
          <Navbar.Brand className="w-100 py-3 px-3 mb-3">
            <Translation textKey="adminTitle" />
          </Navbar.Brand>
          
          {/* Back to Home Link */}
          <a
            href="/"
            className="back-to-home nav-link w-100 mb-3"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="icon"><FaArrowLeft /></span>
            <span className="label"><Translation textKey="backToWebsite" /></span>
          </a>
          
          <Nav className="flex-column w-100">
            {menuItems.map((item) => (
              <Nav.Link
                key={item.path}
                as={Link}
                to={item.path}
                className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={closeMobileSidebar}
              >
                <span className="icon">{item.icon}</span>
                <span className="label"><Translation textKey={item.labelKey} /></span>
              </Nav.Link>
            ))}
          </Nav>
          
          {/* Logout Button */}
          <div className="mt-auto w-100 px-2 mb-3">
            <Nav.Link 
              className="sidebar-link logout-button d-flex align-items-center justify-content-center"
              onClick={handleLogout}
            >
              <span className="icon"><FaSignOutAlt /></span>
              <span className="label"><Translation textKey="logout" /></span>
            </Nav.Link>
          </div>
        </Container>
      </Navbar>
      <div className={`admin-content ${mobileOpen ? 'sidebar-open' : ''}`}>
        {children}
      </div>
    </div>
  );
};

export default AdminLayout;
